import React, { useState, useEffect, useRef } from "react";
import { db } from "../../firebase/config";
import { doc, deleteDoc, updateDoc, getDoc } from "firebase/firestore";
import { useAuth } from "../Auth/AuthProvider";
import styles from "./ProjectLeftSidebar.module.css";
import { useStripe } from "../../hooks/useStripe";
import { getFunctions, httpsCallable } from "firebase/functions";
import linkIconLight from "../../assets/link_light.svg";
import linkIconDark from "../../assets/link_dark.svg";
import { createPortal } from "react-dom";

import shareIconLight from "../../assets/share_light.svg";
import shareIconDark from "../../assets/share_dark.svg";
import referralCopyIconLight from "../../assets/copy_item_dark.svg";
import referralCopyIconDark from "../../assets/copy_item_light.svg";
import referralCheckIconLight from "../../assets/copy_light.svg";
import referralCheckIconDark from "../../assets/copy_dark.svg";
import redeemIconLight from "../../assets/redeem_light.svg";
import redeemIconDark from "../../assets/redeem_dark.svg";
import closeIconLight from "../../assets/close_light.svg";
import closeIconDark from "../../assets/close_dark.svg";
import { useTheme } from "../../App";

import iconImage from "../../assets/diffusion.svg";
import addIconLight from "../../assets/add_light.svg";
import addIconDark from "../../assets/add_dark.svg";

import appsIconLight from "../../assets/apps_light.svg";
import appsIconDark from "../../assets/apps_dark.svg";

import parametricIconLight from "../../assets/parametric_light.svg";
import parametricIconDark from "../../assets/parametric_dark.svg";

import dockToLeftIconLight from "../../assets/dock_to_left_light.svg";
import dockToLeftIconDark from "../../assets/dock_to_left_dark.svg";

import dockToRightIcon from "../../assets/dock_to_right.svg";
import ellipsisIconLight from "../../assets/ellipsis_light.svg";
import ellipsisIconDark from "../../assets/ellipsis_dark.svg";

import renameIconLight from "../../assets/rename_light.svg";
import renameIconDark from "../../assets/rename_dark.svg";

import copyIcon from "../../assets/share.svg";
import checkIcon from "../../assets/check.svg";
import copyLinkIconLight from "../../assets/copy_light.svg";
import copyLinkIconDark from "../../assets/copy_dark.svg";

import deleteIcon from "../../assets/delete-red.svg";
import diffusionIconLight from "../../assets/diffusion_lighter.svg";
import diffusionIconDark from "../../assets/diffusion_darker.svg";
import chevronIconLight from "../../assets/chevron_light.svg";
import chevronIconDark from "../../assets/chevron_dark.svg";

import logoutIconLight from "../../assets/logout_light.svg";
import logoutIconDark from "../../assets/logout_dark.svg";

import policyIconLight from "../../assets/policy_light.svg";
import policyIconDark from "../../assets/policy_dark.svg";

import termsIconLight from "../../assets/terms_light.svg";
import termsIconDark from "../../assets/terms_dark.svg";

import discordIconLight from "../../assets/discord_light.svg";
import discordIconDark from "../../assets/discord_dark.svg";

import feedbackIconLight from "../../assets/feedback_light.svg";
import feedbackIconDark from "../../assets/feedback_dark.svg";

import accountIconLight from "../../assets/settings_light.svg";
import accountIconDark from "../../assets/settings_dark.svg";

import sendIconLight from "../../assets/send_light.svg";
import sendIconDark from "../../assets/send_dark.svg";

import checkoutIconLight from "../../assets/checkout_light.svg";
import checkoutIconDark from "../../assets/checkout_dark.svg";

import { useThemedIcon } from "../../hooks/useThemedIcon";
import ShareModal from "../ShareModal/ShareModal";

import emailIconLight from "../../assets/mail_light.svg";
import emailIconDark from "../../assets/mail_dark.svg";

import infinityIconLight from "../../assets/infinity_light.svg";
import infinityIconDark from "../../assets/infinity_dark.svg";

import speedIconLight from "../../assets/speed_light.svg";
import speedIconDark from "../../assets/speed_dark.svg";

import giftIconLight from "../../assets/gift_light.svg";
import giftIconDark from "../../assets/gift_dark.svg";

import creditsIconLight from "../../assets/credits_light.svg";
import creditsIconDark from "../../assets/credits_dark.svg";

import billingIconLight from "../../assets/billing_light.svg";
import billingIconDark from "../../assets/billing_dark.svg";

import themeIconLight from "../../assets/theme_light.svg";
import themeIconDark from "../../assets/theme_dark.svg";

// Light mode icon variants
import lightIconActiveLightTheme from "../../assets/light_icon_active_light.svg";
import lightIconActiveDarkTheme from "../../assets/light_icon_active_dark.svg";
import lightIconInactiveLightTheme from "../../assets/light_icon_inactive_light.svg";
import lightIconInactiveDarkTheme from "../../assets/light_icon_inactive_dark.svg";

// Dark mode icon variants
import darkIconActiveLightTheme from "../../assets/dark_icon_active_light.svg";
import darkIconActiveDarkTheme from "../../assets/dark_icon_active_dark.svg";
import darkIconInactiveLightTheme from "../../assets/dark_icon_inactive_light.svg";
import darkIconInactiveDarkTheme from "../../assets/dark_icon_inactive_dark.svg";
import loadingIcon from "../../assets/loading.svg";
import loadIconLight from "../../assets/loading.svg";
import loadIconDark from "../../assets/load_dark.svg";
import billingLoadLight from "../../assets/billingLoad_light.svg";
import billingLoadDark from "../../assets/billingLoad_dark.svg";

interface Project {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  userId: string;
  thumbnail?: string;
  description?: string;
}

interface ExpandableProjectSidebarProps {
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isMobileOpen?: boolean;
  onToggleMobile?: () => void;
  isResizing?: boolean;
  projects: Project[];
  onProjectSelect: (projectId: string) => void;
  onNewProject: () => void;
  onProjectDeleted: (projectId: string) => void;
  onProjectRenamed?: (projectId: string, newName: string) => void;
  loading?: boolean;
  onNavigateToModels?: () => void;
  onNavigateToSettings?: () => void;
  connectionStatus?: "unknown" | "connected" | "error";
  mobileBreakpoint?: number;
  forceVisibleCollapsed?: boolean; // ← ADD THIS NEW PROP
}
const ConfirmDeleteModal: React.FC<{
  isOpen: boolean;
  projectName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}> = ({ isOpen, projectName, onConfirm, onCancel, isDeleting = false }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Delete model</h3>
        <p className={styles.modalText}>
          This will delete <strong>{projectName}</strong>
        </p>
        <p className={styles.modalSubtext}>
          Are you sure you want to permanently delete this model and all its
          data? This action cannot be undone.
        </p>
        <div className={styles.modalButtons}>
          {/* <button
            className={styles.modalCancelButton}
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </button> */}
          <button
            className={styles.modalDeleteButton}
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                Deleting...
                <img
                  src={loadingIcon}
                  alt="Deleting"
                  className={styles.deletingIcon}
                />
              </>
            ) : (
              "Delete model"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
const CreditsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const { getIcon } = useThemedIcon();
  const closeIcon = getIcon(closeIconLight, closeIconDark);
  const sendIcon = getIcon(sendIconLight, sendIconDark);
  const billingLoad = getIcon(billingLoadLight, billingLoadDark);
  const [isAnnual, setIsAnnual] = useState(false);

  // Destructure the new function here
  const { subscribeToPlan, loading: stripeLoading } = useStripe();
  const { user } = useAuth();
  const [loadingPackage, setLoadingPackage] = useState<string | null>(null);

  // YOUR STRIPE PRICE IDs
  const PRICE_ID_HOBBYIST_ANNUAL = "price_1T1AXiI5kkwS8iLKQhCQ3z8k";
  const PRICE_ID_PRO_ANNUAL = "price_1T1AYHI5kkwS8iLKREtjD1p9";

  const PRICE_ID_HOBBYIST_MONTHLY = "price_1T15YtI5kkwS8iLKnVX3pW1a";
  const PRICE_ID_PRO_MONTHLY = "price_1T15ZcI5kkwS8iLKVLUZD1Zv";

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubscribe = async (tier: "hobbyist" | "pro") => {
    // Logic to choose the ID
    const selectedPriceId = isAnnual
      ? tier === "pro"
        ? PRICE_ID_PRO_ANNUAL
        : PRICE_ID_HOBBYIST_ANNUAL
      : tier === "pro"
        ? PRICE_ID_PRO_MONTHLY
        : PRICE_ID_HOBBYIST_MONTHLY;

    // Send to your existing Cloud Function
    await subscribeToPlan(selectedPriceId);
  };

  const isAnyLoading = loadingPackage !== null;

  return createPortal(
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div
        className={styles.creditsModalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Upgrade your plan</h3>
          <button
            className={styles.modalCloseButton}
            onClick={onClose}
            aria-label="Close"
          >
            <img src={closeIcon} alt="Close" className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Monthly/Annual Toggle */}
          <div className={styles.toggleContainer}>
            <span
              className={
                !isAnnual ? styles.activeToggleLabel : styles.toggleLabel
              }
            >
              Monthly
            </span>
            <button
              className={styles.toggleButton}
              onClick={() => setIsAnnual(!isAnnual)}
              type="button"
            >
              <div
                className={`${styles.toggleSwitch} ${isAnnual ? styles.toggleSwitchAnnual : ""}`}
              />
            </button>
            <span
              className={
                isAnnual ? styles.activeToggleLabel : styles.toggleLabel
              }
            >
              Annual <span className={styles.savingsBadge}>Save 40%</span>
            </span>
          </div>

          <div className={styles.creditsGrid}>
            {/* Hobbyist */}
            <div className={styles.creditCard}>
              <div className={styles.creditCardHeader}>
                <div className={styles.creditCardTitle}>Hobbyist</div>
                <div className={styles.creditCardPrice}>
                  {isAnnual ? "$5.99 / month" : "$9.99 / month"}
                </div>
                {isAnnual && (
                  <div className={styles.billedAnnually}>
                    Billed annually ($71.88)
                  </div>
                )}
              </div>
              <div className={styles.creditCardDescription}>
                For exploring ideas.
                <br />• <strong>150 Rollover Credits</strong> / mo
                <br />• <strong>5 Daily Free Credits</strong>
              </div>
              <button
                className={styles.creditCardButton}
                onClick={() => handleSubscribe("hobbyist")}
                disabled={isAnyLoading}
              >
                <span>Subscribe</span>
                <img
                  src={loadingPackage === "hobbyist" ? billingLoad : sendIcon}
                  alt=""
                  className={`${styles.creditCardIcon} ${
                    loadingPackage === "hobbyist" ? styles.spinning : ""
                  }`}
                />
              </button>
            </div>

            {/* Pro */}
            <div className={styles.creditCard}>
              <div className={styles.creditCardHeader}>
                <div className={styles.creditCardTitle}>Pro</div>
                <div className={styles.creditCardPrice}>
                  {isAnnual ? "$17.99 / month" : "$29.99 / month"}
                </div>
                {isAnnual && (
                  <div className={styles.billedAnnually}>
                    Billed annually ($215.88)
                  </div>
                )}
              </div>
              <div className={styles.creditCardDescription}>
                For consistent builders.
                <br />• <strong>Unlimited Credits</strong>
                <br />• Priority Processing
              </div>
              <button
                className={styles.creditCardButton}
                onClick={() => handleSubscribe("pro")}
                disabled={isAnyLoading}
              >
                <span>Subscribe</span>
                <img
                  src={loadingPackage === "pro" ? billingLoad : sendIcon}
                  alt=""
                  className={`${styles.creditCardIcon} ${
                    loadingPackage === "pro" ? styles.spinning : ""
                  }`}
                />
              </button>
            </div>

            {/* Enterprise */}
            <div className={styles.creditCard}>
              <div className={styles.creditCardHeader}>
                <div className={styles.creditCardTitle}>Enterprise</div>
                <div className={styles.creditCardPrice}>Custom</div>
              </div>
              <div className={styles.creditCardDescription}>
                For organizations with advanced needs, offering tailored plans
                for scale, support, and custom workflows.
              </div>
              <a
                href="mailto:hello@noahcad.com"
                className={styles.creditCardButton}
                style={{ textDecoration: "none" }}
              >
                <span>Contact us</span>
                <img src={sendIcon} alt="" className={styles.creditCardIcon} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const ReferralModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const { getIcon } = useThemedIcon();
  const shareIcon = getIcon(shareIconLight, shareIconDark);
  const giftIcon = getIcon(giftIconLight, giftIconDark);
  const sendIcon = getIcon(sendIconLight, sendIconDark);
  const redeemIcon = getIcon(redeemIconLight, redeemIconDark);
  const linkIcon = getIcon(linkIconLight, linkIconDark);
  const copyLinkIcon = getIcon(copyLinkIconLight, copyLinkIconDark);
  const referralCopyIcon = getIcon(referralCopyIconLight, referralCopyIconDark);
  const referralCheckIcon = getIcon(
    referralCheckIconLight,
    referralCheckIconDark,
  );
  const closeIcon = getIcon(closeIconLight, closeIconDark); // ADD THIS LINE

  const { userProfile } = useAuth();
  const [isCopied, setIsCopied] = useState(false);
  const referralCode = userProfile?.referralCode
    ? `${window.location.origin}/?ref=${userProfile.referralCode}`
    : "Loading...";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralCode);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div
        className={styles.referralModalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Earn 25+ credits</h3>
          <button
            className={styles.modalCloseButton}
            onClick={onClose}
            aria-label="Close"
          >
            <img src={closeIcon} alt="Close" className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.referralLinkBox}>
            <img
              src={linkIcon}
              alt="Link"
              className={styles.referralLinkIcon}
            />
            <span className={styles.referralLinkText}>{referralCode}</span>
            <button
              className={styles.referralCopyButton}
              onClick={handleCopyLink}
            >
              <img
                src={isCopied ? referralCheckIcon : referralCopyIcon}
                alt={isCopied ? "Copied" : "Copy"}
                className={styles.referralCheckIcon}
              />
              {isCopied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className={styles.referralDivider}></div>

          <div className={styles.referralSteps}>
            <div className={styles.referralStep}>
              <img
                src={shareIcon}
                alt="Share"
                className={styles.referralStepIcon}
              />
              <span className={styles.referralStepText}>
                Share your invite link
              </span>
            </div>
            <div className={styles.referralStep}>
              <img
                src={redeemIcon}
                alt="Redeem"
                className={styles.referralStepIcon}
              />
              <span className={styles.referralStepText}>
                They sign up and get <strong>25 extra credits</strong>
              </span>
            </div>
            <div className={styles.referralStep}>
              <img
                src={giftIcon}
                alt="Gift"
                className={styles.referralStepIcon}
              />
              <span className={styles.referralStepText}>
                You get <strong>25 credits</strong> when they purchase their
                first credit package (e.g. 100 credits/$5)
              </span>
            </div>
          </div>

          <button className={styles.referralTermsButton}>
            View terms and conditions
            <img
              src={sendIcon}
              alt="External link"
              className={styles.referralTermsIcon}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export const SidebarControlButtons: React.FC<{
  isExpanded: boolean;
  onToggle: () => void;
  isMobileOpen?: boolean;
  onToggleMobile?: () => void;
  isResizing?: boolean;
  mobileBreakpoint?: number; // ADD THIS LINE
}> = ({
  isExpanded,
  onToggle,
  isMobileOpen = false,
  onToggleMobile,
  isResizing = false,
  mobileBreakpoint = 960, // ADD THIS LINE with default value
}) => {
  const { getIcon } = useThemedIcon();
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" && window.innerWidth <= mobileBreakpoint, // CHANGE from 960 to mobileBreakpoint
  );

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint); // CHANGE from 960 to mobileBreakpoint
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileBreakpoint]); // ADD mobileBreakpoint to dependency array

  const dockToLeftIcon = getIcon(dockToLeftIconLight, dockToLeftIconDark);

  const handleClick = () => {
    // console.log("Button clicked:", {
    //   isMobile,
    //   hasOnToggleMobile: !!onToggleMobile,
    //   isMobileOpen,
    // });

    if (isMobile && onToggleMobile) {
      // console.log("Calling onToggleMobile");
      onToggleMobile();
    } else {
      // console.log("Calling onToggle");
      onToggle();
    }
  };

  // On mobile, use isMobileOpen to determine button position
  // On desktop, use isExpanded
  const buttonClass = isMobile
    ? isMobileOpen
      ? styles.closeSidebarButton
      : styles.openSidebarButton
    : isExpanded
      ? styles.closeSidebarButton
      : styles.openSidebarButton;

  return (
    <button
      className={`${styles.sidebarControlButton} ${buttonClass} ${
        isResizing ? styles.resizing : ""
      }`}
      style={isMobile && isMobileOpen ? { left: "auto", right: "1rem" } : {}}
      onClick={handleClick}
    >
      <img
        src={dockToLeftIcon}
        alt={isExpanded ? "Close sidebar" : "Open sidebar"}
        className={styles.sidebarControlIcon}
      />
    </button>
  );
};

const ExpandableProjectSidebar: React.FC<ExpandableProjectSidebarProps> = ({
  isExpanded,
  onToggleExpanded,
  isMobileOpen = false,
  onToggleMobile,
  isResizing = false,
  projects,
  onProjectSelect,
  onNewProject,
  onProjectDeleted,
  onProjectRenamed,
  loading = false,
  onNavigateToModels,
  onNavigateToSettings,
  connectionStatus,
  mobileBreakpoint = 960,
  forceVisibleCollapsed = false, // ← ADD THIS
}) => {
  const { getIcon } = useThemedIcon();
  const diffusionIcon = getIcon(diffusionIconLight, diffusionIconDark);
  const addIcon = getIcon(addIconLight, addIconDark);
  const appsIcon = getIcon(appsIconLight, appsIconDark);
  const parametricIcon = getIcon(parametricIconLight, parametricIconDark);
  const emailIcon = getIcon(emailIconLight, emailIconDark);
  const sendIcon = getIcon(sendIconLight, sendIconDark);
  const ellipsisIcon = getIcon(ellipsisIconLight, ellipsisIconDark);
  const renameIcon = getIcon(renameIconLight, renameIconDark);
  const copyIcon = getIcon(shareIconLight, shareIconDark);
  const shareIcon = getIcon(shareIconLight, shareIconDark);
  const closeIcon = getIcon(closeIconLight, closeIconDark);

  const creditsIcon = getIcon(creditsIconLight, creditsIconDark);
  const chevronIcon = getIcon(chevronIconLight, chevronIconDark);
  const giftIcon = getIcon(giftIconLight, giftIconDark);
  const loadIcon = getIcon(loadIconLight, loadIconDark);
  const themeIcon = getIcon(themeIconLight, themeIconDark);
  const billingIcon = getIcon(billingIconLight, billingIconDark);
  const accountIcon = getIcon(accountIconLight, accountIconDark);
  const termsIcon = getIcon(termsIconLight, termsIconDark);
  const billingLoad = getIcon(billingLoadLight, billingLoadDark);
  const policyIcon = getIcon(policyIconLight, policyIconDark);
  const logoutIcon = getIcon(logoutIconLight, logoutIconDark);
  const discordIcon = getIcon(discordIconLight, discordIconDark);
  const feedbackIcon = getIcon(feedbackIconLight, feedbackIconDark);
  const getLightModeIcon = () => {
    const isLightModeActive = activeTheme === "light";
    const currentTheme = activeTheme;

    if (currentTheme === "light") {
      return isLightModeActive
        ? lightIconActiveLightTheme
        : lightIconInactiveLightTheme;
    } else {
      return isLightModeActive
        ? lightIconActiveDarkTheme
        : lightIconInactiveDarkTheme;
    }
  };

  const getDarkModeIcon = () => {
    const isDarkModeActive = activeTheme === "dark";
    const currentTheme = activeTheme;

    if (currentTheme === "light") {
      return isDarkModeActive
        ? darkIconActiveLightTheme
        : darkIconInactiveLightTheme;
    } else {
      return isDarkModeActive
        ? darkIconActiveDarkTheme
        : darkIconInactiveDarkTheme;
    }
  };
  const [showReferralModal, setShowReferralModal] = useState(false);
  const { logout, user, userProfile, updateUserProfile } = useAuth(); // ADD updateUserProfile here

  const { purchaseCredits, loading: stripeLoading } = useStripe();
  const [showTopBorder, setShowTopBorder] = useState(false);
  const [showBottomBorder, setShowBottomBorder] = useState(false);
  const [showBottomSectionBorder, setShowBottomSectionBorder] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== "undefined" && window.innerWidth <= mobileBreakpoint,
  );
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= mobileBreakpoint);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileBreakpoint]); // ADD mobileBreakpoint to dependency array

  const [hideText, setHideText] = useState(!isExpanded);
  const [textOpacity, setTextOpacity] = useState(isExpanded ? 1 : 0);
  const [openProjectDropdown, setOpenProjectDropdown] = useState<string | null>(
    null,
  );
  const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  // ADD THESE THREE LINES:

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareProjectId, setShareProjectId] = useState<string | null>(null);
  const [shareProjectData, setShareProjectData] = useState<{
    isPublic: boolean;
    shareToken?: string;
    name: string;
  } | null>(null);
  const { theme: activeTheme, setTheme: setActiveTheme } = useTheme();
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const projectsListRef = useRef<HTMLDivElement>(null);
  const projectInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>(
    {},
  ); // Handle text visibility and opacity with proper timing
  useEffect(() => {
    const shouldShowText = isMobileView ? isMobileOpen : isExpanded;

    if (shouldShowText) {
      // When showing text, do it immediately with no delay
      setHideText(false);
      setTextOpacity(1);
    } else {
      // When hiding text, fade out first, then hide
      setTextOpacity(0);
      const timer = setTimeout(() => {
        setHideText(true);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isExpanded, isMobileOpen, isMobileView]);
  // Force immediate text display when mobile sidebar opens
  useEffect(() => {
    if (isMobileView && isMobileOpen) {
      setHideText(false);
      setTextOpacity(1);
    }
  }, [isMobileView, isMobileOpen]);
  // Add click outside handler effect
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Check if click is on modal overlay - if so, don't close profile dropdown
      const isModalOverlay = target.closest(`.${styles.modalOverlay}`);

      // Handle project dropdown clicks
      if (openProjectDropdown) {
        const projectDropdown = document.querySelector(
          `[data-project-dropdown-id="${openProjectDropdown}"]`,
        );
        const ellipsisButton = target.closest(
          `.${styles.projectEllipsisButton}`,
        );

        if (ellipsisButton) {
          return;
        }

        // Don't close project dropdown if clicking on modal overlay or if any modal is open
        if (isModalOverlay || showDeleteModal || showShareModal) {
          return;
        }

        if (projectDropdown && !projectDropdown.contains(target)) {
          setOpenProjectDropdown(null);
        }
      }

      // Handle profile dropdown clicks
      if (showProfileDropdown) {
        const profileDropdown = document.querySelector(
          `.${styles.profileDropdown}`,
        );
        const profileButton = target.closest(`.${styles.profileButton}`);

        if (profileButton) {
          return;
        }

        // Don't close profile dropdown if clicking on modal overlay or modal content
        // OR if any modal is currently open
        if (
          isModalOverlay ||
          showCreditsModal ||
          showReferralModal ||
          showFeedbackModal
        ) {
          return;
        }

        if (profileDropdown && !profileDropdown.contains(target)) {
          setShowProfileDropdown(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    openProjectDropdown,
    showProfileDropdown,
    showCreditsModal,
    showReferralModal,
    showFeedbackModal,
    showDeleteModal,
    showShareModal,
  ]);
  const checkOverflow = () => {
    if (!projectsListRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = projectsListRef.current;
    const threshold = 12;

    const hasVerticalScroll = scrollHeight > clientHeight;

    if (!hasVerticalScroll) {
      setShowTopBorder(false);
      setShowBottomBorder(false);
      setShowBottomSectionBorder(false);
      return;
    }

    setShowTopBorder(scrollTop > threshold);
    const isNearBottom = scrollTop >= scrollHeight - clientHeight - threshold;
    setShowBottomBorder(!isNearBottom);
    setShowBottomSectionBorder(!isNearBottom);
  };

  useEffect(() => {
    checkOverflow();
  }, [projects, loading, isExpanded]);

  const handleProjectsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const threshold = 12;

    const hasVerticalScroll = scrollHeight > clientHeight;

    if (!hasVerticalScroll) {
      setShowTopBorder(false);
      setShowBottomBorder(false);
      setShowBottomSectionBorder(false);
      return;
    }

    setShowTopBorder(scrollTop > threshold);
    const isNearBottom = scrollTop >= scrollHeight - clientHeight - threshold;
    setShowBottomBorder(!isNearBottom);
    setShowBottomSectionBorder(!isNearBottom);
  };

  const handleProjectEllipsisClick = (
    e: React.MouseEvent,
    projectId: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setOpenProjectDropdown((prevState) => {
      const newState = prevState === projectId ? null : projectId;
      return newState;
    });
  };

  const handleProfileClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowProfileDropdown(!showProfileDropdown);
  };

  const handleAccountClick = () => {
    if (onNavigateToSettings) {
      onNavigateToSettings();
    }
    setShowProfileDropdown(false);
  };

  // const handleAccountClick = (e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   if (onNavigateToSettings) {
  //     onNavigateToSettings();
  //   }
  //   setShowProfileDropdown(false);
  // };

  const handleBillingClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsBillingLoading(true);

    // Store the current URL before going to Stripe billing
    sessionStorage.setItem("billing_return_url", window.location.href);
    // console.log("💾 Stored billing return URL:", window.location.href);

    try {
      const functions = getFunctions();
      const createCustomerPortalSession = httpsCallable(
        functions,
        "createCustomerPortalSession",
      );

      // Return to root with a flag - we'll handle the redirect back to the original URL
      const { data } = await createCustomerPortalSession({
        returnUrl: window.location.origin + "/?billing_return=true",
      });

      window.location.href = (data as any).url;
    } catch (error) {
      // console.error("Error opening customer portal:", error);
      setIsBillingLoading(false);
    }
  };

  const handleThemeToggle = async (
    theme: "light" | "dark",
    e?: React.MouseEvent,
  ) => {
    if (e) {
      e.stopPropagation();
    }

    // Disable transitions temporarily
    document.documentElement.classList.add("theme-transitioning");

    setActiveTheme(theme);

    // Re-enable transitions after theme has been applied
    setTimeout(() => {
      document.documentElement.classList.remove("theme-transitioning");
    }, 0);

    // Save to Firestore
    if (userProfile) {
      try {
        await updateUserProfile({ themePreference: theme });
      } catch (error) {
        // console.error("Error saving theme preference:", error);
      }
    }
  };

  const handleCreditsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCreditsModal(true);
    // Don't close the profile dropdown
    // setShowProfileDropdown(false); // Remove this line
  };

  const handleFreeCreditsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReferralModal(true);
    // Don't close the profile dropdown
    // setShowProfileDropdown(false); // Remove this line
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowProfileDropdown(false);
      // Redirect to login page after logout
      window.history.pushState({}, "", "/login");
      window.location.reload();
    } catch (error) {
      // console.error("Error logging out:", error);
    }
  };

  const handleJoinCommunity = () => {
    window.open("https://discord.gg/mYG5ZDfmP7", "_blank");
    setShowProfileDropdown(false);
  };

  const handleSendFeedback = () => {
    setShowFeedbackModal(true);
  };

  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;

    setIsSendingFeedback(true);
    try {
      const functions = getFunctions();
      const sendFeedbackEmail = httpsCallable(functions, "sendFeedbackEmail");

      await sendFeedbackEmail({
        feedback: feedbackText,
        userEmail: user?.email || "anonymous",
        userName:
          userProfile?.fullName || user?.displayName || "Anonymous User",
      });

      setFeedbackSubmitted(true);
    } catch (error) {
      // console.error("Error sending feedback:", error);
      // alert("Failed to send feedback. Please try again.");
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackText("");
    setFeedbackSubmitted(false);
  };

  const handleProjectRename = (projectId: string) => {
    setEditingProjectId(projectId);
    setOpenProjectDropdown(null);
  };

  const handleProjectNameKeyDown = async (
    event: React.KeyboardEvent,
    projectId: string,
  ) => {
    if (event.key === "Enter") {
      const input = projectInputRefs.current[projectId];
      if (input) {
        const newName = input.value.trim();
        if (newName) {
          try {
            await updateDoc(doc(db, "projects", projectId), {
              name: newName,
              updatedAt: new Date(),
            });

            if (onProjectRenamed) {
              onProjectRenamed(projectId, newName);
            }

            // console.log("Renamed project to:", newName);
          } catch (error) {
            // console.error("Error renaming project:", error);
          }
        }
      }
      setEditingProjectId(null);
    } else if (event.key === "Escape") {
      setEditingProjectId(null);
    }
  };

  const handleProjectNameBlur = async (projectId: string) => {
    const input = projectInputRefs.current[projectId];
    if (input) {
      const newName = input.value.trim();
      if (newName) {
        try {
          await updateDoc(doc(db, "projects", projectId), {
            name: newName,
            updatedAt: new Date(),
          });

          if (onProjectRenamed) {
            onProjectRenamed(projectId, newName);
          }

          // console.log("Renamed project to:", newName);
        } catch (error) {
          // console.error("Error renaming project:", error);
        }
      }
    }
    setEditingProjectId(null);
  };

  const handleProjectCopy = (project: Project) => {
    navigator.clipboard.writeText(project.name);
    setCopiedStates((prev) => ({ ...prev, [project.id]: true }));
    setTimeout(() => {
      setCopiedStates((prev) => ({ ...prev, [project.id]: false }));
    }, 2000);
  };

  const handleProjectDelete = (project: Project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
    // setOpenProjectDropdown(null);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, "projects", projectToDelete.id));
      onProjectDeleted(projectToDelete.id);
    } catch (error) {
      // console.error("Error deleting project:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setProjectToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  // ADD THIS FUNCTION:
  const loadProjectShareStatus = async (projectId: string) => {
    try {
      const projectDoc = await getDoc(doc(db, "projects", projectId));
      if (projectDoc.exists()) {
        const data = projectDoc.data();
        setShareProjectData({
          isPublic: data.isPublic || false,
          shareToken: data.shareToken,
          name: data.name || "Project",
        });
      }
    } catch (error) {
      // console.error("Error loading project share status:", error);
    }
  };

  const handleProjectShare = (project: Project) => {
    setShareProjectId(project.id);
    loadProjectShareStatus(project.id);
    setShowShareModal(true);
    // setOpenProjectDropdown(null);
  };

  const handleMainIconClick = () => {
    onNewProject();
  };

  const handleModelsClick = () => {
    if (onNavigateToModels) {
      onNavigateToModels();
    }
  };

  // Helper function to get auth provider icon
  // Helper function to get auth provider icon
  const getAuthProviderIcon = (): {
    type: "svg" | "component";
    icon: string | React.ReactElement;
  } => {
    if (!user)
      return {
        type: "component",
        icon: (
          <img
            src={emailIcon}
            alt="Email"
            style={{ width: "1rem", height: "1rem" }}
          />
        ),
      };

    if (user.providerData && user.providerData.length > 0) {
      const providerId = user.providerData[0].providerId;
      switch (providerId) {
        case "google.com":
          return {
            type: "component",
            icon: (
              <svg width="1rem" height="1rem" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            ),
          };
        case "microsoft.com":
          return {
            type: "component",
            icon: (
              <svg width="1rem" height="1rem" viewBox="0 0 24 24">
                <path fill="#F25022" d="M1 1h10v10H1z" />
                <path fill="#00A4EF" d="M13 1h10v10H13z" />
                <path fill="#7FBA00" d="M1 13h10v10H1z" />
                <path fill="#FFB900" d="M13 13h10v10H13z" />
              </svg>
            ),
          };
        case "apple.com":
          return {
            type: "component",
            icon: (
              <svg
                width="1rem"
                height="1rem"
                viewBox="0 0 24 24"
                fill="var(--color-black)"
              >
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
            ),
          };
        default:
          return {
            type: "component",
            icon: (
              <img
                src={emailIcon}
                alt="Email"
                style={{ width: "1rem", height: "1rem" }}
              />
            ),
          };
      }
    }
    return {
      type: "component",
      icon: (
        <img
          src={emailIcon}
          alt="Email"
          style={{ width: "1rem", height: "1rem" }}
        />
      ),
    };
  };

  // Helper function to get user profile image or initials
  // Helper function to get user profile image or initials
  // Helper function to get user profile image or initials
  const getUserAvatar = () => {
    const displayName =
      userProfile?.fullName ||
      user?.displayName ||
      user?.email?.split("@")[0] ||
      "User";

    let photoURL = userProfile?.photoURL || user?.photoURL;

    // ADD THIS DEBUG LOG
    // console.log("getUserAvatar called:", {
    //   hasUserProfile: !!userProfile,
    //   hasUser: !!user,
    //   userProfilePhotoURL: userProfile?.photoURL,
    //   userPhotoURL: user?.photoURL,
    //   finalPhotoURL: photoURL,
    // });

    if (photoURL) {
      photoURL = photoURL.trim();
      if (photoURL === "" || photoURL === "null" || photoURL === "undefined") {
        photoURL = null;
      }
    }

    if (photoURL) {
      return {
        type: "image" as const,
        value: photoURL,
      };
    }

    const initials = displayName.charAt(0).toUpperCase();
    return {
      type: "initials" as const,
      value: initials,
    };
  };

  // Add this helper function near the top of the component, after the other helper functions
  const groupProjectsByDate = (projects: Project[]) => {
    const groups: { [key: string]: Project[] } = {};

    projects.forEach((project) => {
      const dateKey = project.createdAt.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(project);
    });

    // Sort groups by date (most recent first)
    const sortedGroups = Object.keys(groups)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map((dateKey) => ({
        date: new Date(dateKey),
        projects: groups[dateKey].sort(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
        ),
      }));

    return sortedGroups;
  };

  const formatGroupDate = (date: Date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const projectDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    if (projectDate.getTime() === today.getTime()) {
      return "Today";
    } else if (projectDate.getTime() === yesterday.getTime()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  // Update the renderProjectItem function to remove ellipsis button
  const renderProjectItem = (project: Project) => {
    const isEditing = editingProjectId === project.id;

    return (
      <button
        key={project.id}
        className={`${styles.projectButton} ${isEditing ? styles.editing : ""}`}
        onClick={() => !isEditing && onProjectSelect(project.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          if (!isEditing) {
            setOpenProjectDropdown(
              openProjectDropdown === project.id ? null : project.id,
            );
          }
        }}
      >
        <img
          src={parametricIcon}
          alt="Project"
          className={styles.projectButtonIcon}
        />
        {!hideText && (
          <>
            {isEditing ? (
              <input
                ref={(el) => {
                  projectInputRefs.current[project.id] = el;
                  if (el) {
                    el.focus();
                    el.select();
                    el.setSelectionRange(0, el.value.length);
                  }
                }}
                type="text"
                defaultValue={project.name}
                onKeyDown={(e) => handleProjectNameKeyDown(e, project.id)}
                onBlur={() => handleProjectNameBlur(project.id)}
                className={styles.projectNameInput}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            ) : (
              <span
                className={styles.projectButtonText}
                style={{ opacity: textOpacity }}
              >
                {project.name}
              </span>
            )}
          </>
        )}
        {hideText && <div className={styles.tooltip}>{project.name}</div>}
      </button>
    );
  };
  const shouldShowProjectsSection =
    (isMobileView ? isMobileOpen : isExpanded) &&
    (loading || projects.length > 0) &&
    !hideText; // Don't show until text is ready
  // console.log("Sidebar Debug:", {
  //   isMobileView,
  //   isMobileOpen,
  //   isExpanded,
  //   hideText,
  //   shouldShowProjectsSection,
  //   projectsLength: projects.length,
  //   loading,
  // });
  const shouldShowProfile = userProfile?.onboardingCompleted;
  const displayName = userProfile?.fullName || user?.displayName || "User";
  const userAvatar = getUserAvatar();
  const hasStripeCustomerId = !!userProfile?.stripeCustomerId; // ADD THIS LINE HERE

  // console.log("Profile rendering:", {
  //   shouldShowProfile,
  //   displayName,
  //   userAvatar,
  //   userProfile: userProfile?.photoURL,
  //   user: user?.photoURL,
  // });
  return (
    <>
      <div
        className={`${styles.sidebar} ${
          isExpanded ? styles.expanded : styles.collapsed
        } ${hideText ? styles.textHidden : ""} ${
          isMobileOpen ? styles.mobileOpen : ""
        } ${isResizing ? styles.resizing : ""}`}
        data-mobile-breakpoint={mobileBreakpoint}
        data-force-show={forceVisibleCollapsed ? "true" : "false"}
        style={{
          ["--mobile-breakpoint" as any]: `${mobileBreakpoint}px`,
        }}
      >
        {/* Header Section */}
        <div className={styles.header}>
          <button className={styles.mainIcon} onClick={handleMainIconClick}>
            <img
              src={diffusionIcon}
              alt="Main"
              className={styles.mainIconSvg}
            />
          </button>
        </div>
        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          <button
            className={`${styles.actionButton} ${
              hideText ? styles.showTooltip : ""
            }`}
            onClick={onNewProject}
          >
            <img
              src={addIcon}
              alt="New model"
              className={styles.actionButtonIcon}
            />
            {!hideText && (
              <span
                className={styles.actionButtonText}
                style={{ opacity: textOpacity }}
              >
                New model
              </span>
            )}
            {hideText && <div className={styles.tooltip}>New model</div>}
          </button>

          <button
            className={`${styles.actionButton} ${
              hideText ? styles.showTooltip : ""
            }`}
            onClick={handleModelsClick}
          >
            <img
              src={appsIcon}
              alt="Models"
              className={styles.actionButtonIcon}
            />
            {!hideText && (
              <span
                className={styles.actionButtonText}
                style={{ opacity: textOpacity }}
              >
                Models
              </span>
            )}
            {hideText && <div className={styles.tooltip}>Models</div>}
          </button>
        </div>
        {/* Separator - only shown when expanded AND there are projects */}
        {/* Separator - only shown when expanded AND there are projects */}
        {shouldShowProjectsSection && (
          <div
            className={`${styles.separator} ${
              showTopBorder ? styles.visible : ""
            }`}
          ></div>
        )}

        {/* Projects Section */}
        {shouldShowProjectsSection && (
          <div className={styles.projectsSection}>
            <div
              className={styles.projectsList}
              ref={projectsListRef}
              onScroll={handleProjectsScroll}
            >
              {loading ? (
                <div className={styles.loading}>
                  <img
                    src={diffusionIcon}
                    alt="Loading"
                    className={styles.loadingIcon}
                  />
                </div>
              ) : (
                <>
                  {groupProjectsByDate(projects).map((group, groupIndex) => (
                    <div key={groupIndex} className={styles.dateGroup}>
                      <div className={styles.dateHeader}>
                        {formatGroupDate(group.date)}
                      </div>
                      <div className={styles.dateGroupProjects}>
                        {group.projects.map(renderProjectItem)}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* Show project icons in collapsed mode - currently commented out */}
        {/* {!isExpanded && projects.length > 0 && (
  <div className={styles.collapsedProjects}>
    {projects.map(renderProjectItem)}
  </div>
)} */}

        {/* Bottom Separator - shown when projects overflow */}
        {shouldShowProjectsSection && (
          <div
            className={`${styles.bottomSeparator} ${
              showBottomSectionBorder ? styles.visible : ""
            }`}
          ></div>
        )}

        {/* Bottom Section - Connection Status and Profile */}
        <div className={styles.bottomSection}>
          {/* Connection Status - Only show in CAD mode (when connectionStatus is provided) */}
          {connectionStatus && (
            <button className={styles.connectionStatusButton}>
              <div
                className={`${styles.connectionStatusIndicator} ${
                  connectionStatus === "connected"
                    ? styles.connectionConnected
                    : connectionStatus === "error"
                      ? styles.connectionDisconnected
                      : styles.connectionConnecting
                }`}
              ></div>
              {hideText && (
                <div className={styles.tooltip}>
                  {connectionStatus === "connected"
                    ? "Connected"
                    : connectionStatus === "error"
                      ? "Disconnected"
                      : "Connecting..."}
                </div>
              )}
            </button>
          )}

          {/* Profile Button - Only show if onboarding is completed */}
          {shouldShowProfile && (
            <button
              className={`${styles.profileButton} ${
                showProfileDropdown ? styles.dropdownOpen : ""
              }`}
              onClick={handleProfileClick}
            >
              <div className={styles.profileCircle}>
                {userAvatar.type === "image" ? (
                  <img
                    src={userAvatar.value}
                    alt="Profile"
                    referrerPolicy="no-referrer"
                    className={styles.profileImage}
                  />
                ) : (
                  <span className={styles.profileInitials}>
                    {userAvatar.value}
                  </span>
                )}
              </div>
              {!hideText && (
                <div
                  className={styles.profileInfo}
                  style={{ opacity: textOpacity }}
                >
                  <span className={styles.profileName}>{displayName}</span>
                </div>
              )}
              {!hideText && (
                <div className={styles.chevronContainer}>
                  <img
                    src={chevronIcon}
                    alt="Profile menu"
                    className={`${styles.chevronIcon} ${
                      showProfileDropdown ? styles.chevronUp : ""
                    }`}
                  />
                </div>
              )}
              {hideText && <div className={styles.tooltip}>{displayName}</div>}

              {/* Profile Dropdown - rest of your existing dropdown code */}
              {showProfileDropdown && (
                <div className={styles.profileDropdown}>
                  {/* Auth Provider Info */}
                  <div className={styles.authInfo}>
                    {(() => {
                      const authIcon = getAuthProviderIcon();
                      if (authIcon.type === "component") {
                        return (
                          <div className={styles.authIcon}>{authIcon.icon}</div>
                        );
                      } else {
                        return (
                          <div className={styles.authIcon}>
                            <img
                              src={authIcon.icon as string}
                              alt="Auth provider"
                            />
                          </div>
                        );
                      }
                    })()}
                    <span className={styles.authEmail}>
                      {user?.email || "user@example.com"}
                    </span>
                  </div>

                  {/* Credits Section */}
                  {/* Credits Section */}
                  <div className={styles.creditsSection}>
                    <div
                      className={styles.creditsButton}
                      onClick={handleCreditsClick}
                    >
                      <div className={styles.creditsLeft}>
                        <img
                          src={creditsIcon}
                          alt="Credits"
                          className={styles.creditsIconImage}
                        />
                        <span className={styles.creditsText}>Credits</span>
                      </div>
                      <div className={styles.creditsRight}>
                        <span className={styles.creditsCount}>
                          {(userProfile?.credits || 0).toLocaleString()} left
                        </span>
                        <img
                          src={chevronIcon}
                          alt="More"
                          className={styles.creditsChevronIcon}
                        />
                      </div>
                    </div>

                    {/* Get free credits button */}
                    <div
                      className={styles.freeCreditsButton}
                      onClick={handleFreeCreditsClick}
                    >
                      <img
                        src={giftIcon}
                        alt="Gift"
                        className={styles.freeCreditsIcon}
                      />
                      <span className={styles.freeCreditsText}>
                        Get free credits
                      </span>
                    </div>
                  </div>

                  <div className={styles.dropdownSeparatorWithGap}></div>

                  {/* Theme - now as a clickable button */}
                  {/* Theme - now as a clickable button */}
                  {/* Theme */}
                  <div
                    className={styles.themeOption}
                    onClick={(e) =>
                      handleThemeToggle(
                        activeTheme === "light" ? "dark" : "light",
                        e,
                      )
                    }
                  >
                    <img
                      src={themeIcon}
                      alt="Theme"
                      className={styles.dropdownIcon}
                    />
                    <span className={styles.dropdownText}>Theme</span>
                    <div className={styles.themeIcons}>
                      <button
                        className={styles.themeIconButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleThemeToggle("light", e);
                        }}
                      >
                        <img
                          src={getLightModeIcon()}
                          alt="Light mode"
                          className={styles.themeIconImage}
                        />
                      </button>
                      <button
                        className={styles.themeIconButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleThemeToggle("dark", e);
                        }}
                      >
                        <img
                          src={getDarkModeIcon()}
                          alt="Dark mode"
                          className={styles.themeIconImage}
                        />
                      </button>
                    </div>
                  </div>
                  {/* Billing - only show if user has stripe customer id */}
                  {hasStripeCustomerId && (
                    <div
                      className={styles.dropdownOption}
                      onClick={handleBillingClick}
                    >
                      <img
                        src={billingIcon}
                        alt="Billing"
                        className={styles.dropdownIcon}
                      />
                      <span className={styles.dropdownText}>Billing</span>
                      <img
                        src={isBillingLoading ? billingLoad : sendIcon}
                        alt={isBillingLoading ? "billingLoad" : "External link"}
                        className={`${styles.sendIcon} ${
                          isBillingLoading ? styles.spinning : ""
                        }`}
                      />
                    </div>
                  )}

                  {/* Account */}
                  <div
                    className={styles.dropdownOption}
                    onClick={handleAccountClick}
                  >
                    <img
                      src={accountIcon}
                      alt="Account"
                      className={styles.dropdownIcon}
                    />
                    <span className={styles.dropdownText}>Settings</span>
                  </div>

                  <div className={styles.dropdownSeparator}></div>

                  {/* Join Community */}
                  <div
                    className={styles.dropdownOption}
                    onClick={handleJoinCommunity}
                  >
                    <img
                      src={discordIcon}
                      alt="Join community"
                      className={styles.dropdownIcon}
                    />
                    <span className={styles.dropdownText}>Join community</span>
                    <img
                      src={sendIcon}
                      alt="External link"
                      className={styles.sendIcon}
                    />
                  </div>

                  {/* Send Feedback */}
                  <div
                    className={styles.dropdownOption}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendFeedback();
                    }}
                  >
                    <img
                      src={feedbackIcon}
                      alt="Send feedback"
                      className={styles.dropdownIcon}
                    />
                    <span className={styles.dropdownText}>Send feedback</span>
                  </div>

                  <div className={styles.dropdownSeparator}></div>

                  {/* Terms of Service */}
                  <div
                    className={styles.dropdownOption}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.history.pushState({}, "", "/terms");
                      window.location.href = "/terms";
                      setShowProfileDropdown(false);
                    }}
                  >
                    <img
                      src={termsIcon}
                      alt="Terms of service"
                      className={styles.dropdownIcon}
                    />
                    <span className={styles.dropdownText}>
                      Terms of service
                    </span>
                    <img
                      src={sendIcon}
                      alt="External link"
                      className={styles.sendIcon}
                    />
                  </div>

                  {/* Privacy Policy */}
                  <div
                    className={styles.dropdownOption}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.history.pushState({}, "", "/privacy");
                      window.location.href = "/privacy";
                      setShowProfileDropdown(false);
                    }}
                  >
                    <img
                      src={policyIcon}
                      alt="Privacy policy"
                      className={styles.dropdownIcon}
                    />
                    <span className={styles.dropdownText}>Privacy policy</span>
                    <img
                      src={sendIcon}
                      alt="External link"
                      className={styles.sendIcon}
                    />
                  </div>

                  <div className={styles.dropdownSeparator}></div>

                  {/* Logout Button */}
                  <div
                    className={styles.dropdownOption}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout();
                    }}
                  >
                    <img
                      src={logoutIcon}
                      alt="Log out"
                      className={styles.dropdownIcon}
                    />
                    <span className={styles.dropdownText}>Logout</span>
                  </div>
                </div>
              )}
            </button>
          )}
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        projectName={projectToDelete?.name || ""}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
      />
      {/* Credits Modal */}
      {/* Credits Modal */}
      <CreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />
      ,{/* Referral Modal */}
      <ReferralModal
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
      />
      {showFeedbackModal && (
        <div className={styles.modalOverlay} onClick={handleCloseFeedbackModal}>
          <div
            className={styles.feedbackModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {feedbackSubmitted ? "Feedback sent!" : "Send feedback"}
              </h3>
              <button
                className={styles.modalCloseButton}
                onClick={handleCloseFeedbackModal}
                aria-label="Close"
              >
                <img src={closeIcon} alt="Close" className={styles.closeIcon} />
              </button>
            </div>

            <div className={styles.modalBody}>
              {feedbackSubmitted ? (
                <>
                  <p className={styles.feedbackSuccessText}>
                    Thank you for helping us improve. Your feedback has been
                    sent to our team.
                  </p>
                  <button
                    className={styles.feedbackSubmitButton}
                    onClick={handleCloseFeedbackModal}
                  >
                    Done
                  </button>
                </>
              ) : (
                <>
                  <textarea
                    className={styles.feedbackTextarea}
                    placeholder="Help us improve by sharing your thoughts and suggestions."
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    rows={8}
                  />
                  <button
                    className={styles.feedbackSubmitButton}
                    onClick={handleSubmitFeedback}
                    disabled={isSendingFeedback || !feedbackText.trim()}
                  >
                    {isSendingFeedback ? "Submitting..." : "Submit"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ADD THIS SHARE MODAL: */}
      {shareProjectId && shareProjectData && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setShareProjectId(null);
            setShareProjectData(null);
          }}
          projectId={shareProjectId}
          projectName={shareProjectData.name}
          isPublic={shareProjectData.isPublic}
          shareToken={shareProjectData.shareToken}
          onShareStatusChange={() => {
            if (shareProjectId) {
              loadProjectShareStatus(shareProjectId);
            }
          }}
        />
      )}
    </>
  );
};

export default ExpandableProjectSidebar;
export { CreditsModal };
